import type { FlairPart } from '../../flair/engine';
import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part } from '../body';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';
import { domeLight, type Ellipse, fromPath, type HeadPicture, inEllipse, mask, type SetFlair } from './mythic';

/**
 * The Chef set (ADR-0160 amended, mythic): the head of a grand kitchen. A tall pleated toque, a twirled moustache,
 * chef's whites with an apron, and the Pepper Mill.
 *
 * The head is a mask over the head that leaves the face open (the head's own: happy eyes, a grin, rosy cheeks): a tall
 * white toque, its pleated drum lit from the top-left with a crease every 4 px, a puffed mushroom crown of four lumps
 * overhanging it (a shadow under its lip), and round the brow a band in the team's colour; dark hair cropped at the
 * back; and over the lip a big black handlebar moustache, its two wings sweeping out and curling up into twirls (its
 * far twirl past the cheek, so it reads at 1:1). Its flair: steam curling up off the crown, the cheeks' rosy glow.
 * The suit is chef's whites: a double-breasted jacket (lit on the near side, its folds in grey), a mandarin collar, the
 * team's neckerchief knotted at the throat, two rows of black knot buttons, the team's apron strings round the waist
 * over a cream apron, black clogs. Its flair: puffs of flour off it on the run, a tea towel in the team's stripes
 * swinging from the apron strings.
 *
 * Rubric (`assetc mythic chef` at 5× and 1:1 on grass, lane and night, both teams, idle, run and dead, beside the
 * Barnstormer): head S4 R5 P4 L4 C4 A4 F4, whites S4 R4 P4 L4 C4 F4. Weakest: whites on whites, the jacket and the
 * toque are one white mass at 1:1 (the band, the neckerchief and the buttons part them); the open face's own blush
 * is the cheeks' only colour on the body (the glow is flair).
 */

// --- The toque -------------------------------------------------------------------------------------------------------

/** The head under it (u forward, v down from its middle): where the hair would be. */
const SKULL: Ellipse = [0, -2, 19.5, 19];
/** The band's top and bottom at u: lower in its middle, as a drum's near rim seen from a little above. */
const sag = (u: number) => 1.4 * (1 - (u / 21) ** 2);
const BAND = [-5, -1] as const;
/** The pleated drum over the band: its foot (the band's top), its lip under the crown, its sides flaring out. */
const DRUM_TOP = -14.5;
const drumSides = (v: number): readonly [number, number] => {
  const t = Math.max(0, Math.min(1, (BAND[0] - v) / (BAND[0] - DRUM_TOP)));
  return [-19 - t * 1.5, 19.5 + t * 2];
};
/** The puffed crown: lumps of starched cotton, the front one last. */
const PUFF: readonly Ellipse[] = [
  [-12, -17, 10, 4.8],
  [13.5, -17.2, 9.8, 4.6],
  [1, -18, 11.5, 4],
  [1, -14.4, 23, 3.2],
];
/** The moustache: each wing a path out from the middle under the nose, out, down and curling up into a twirl. */
const WINGS = [
  [6.5, 11.4, 2.5, 12.6, -1.8, 12.6, -5, 11, -5.8, 8.6, -4, 7.4, -2.6, 8.2],
  [6.5, 11.4, 11, 12.6, 15.8, 12.2, 19.2, 10, 19.6, 7.4, 17.8, 6.6, 16.6, 7.6],
] as const;

/** White cotton at light `l`. */
const cotton = (l: number): PaletteKey => (l > 0.62 ? 'w' : l > 0.2 ? '5' : l > -0.2 ? '4' : '3');

const toque: HeadPicture = (u, v, lx, ly) => {
  // The moustache over everything under it: black, its top caught by the light, thicker in its middle.
  for (const wing of WINGS) {
    const d = fromPath(u, v, wing);
    const out = Math.hypot(u - 6.5, v - 11.2);
    const thick = Math.max(0.85, 2.8 - out * 0.13);
    if (d < thick) {
      if (out < 2.6) return v < 10 ? '2' : v > 12.4 ? 'k' : '1';
      return d < thick - 0.9 && v < fromPathY(u, wing) ? '2' : v > fromPathY(u, wing) + 0.8 ? 'k' : '1';
    }
  }
  // The puffed crown: the lump it is most inside of lights it; a crease where two lumps meet.
  let best = Infinity;
  let second = Infinity;
  let lump: Ellipse | null = null;
  for (const e of PUFF) {
    const d = inEllipse(u, v, e);
    if (d < best) [second, best, lump] = [best, d, e];
    else if (d < second) second = d;
  }
  if (lump && best <= 1) {
    if (second < 1 && second - best < 0.3 && v < -15.2) return '4';
    // Its lip in shade where it hangs over the drum.
    if (lump === PUFF[3] && v > -13.8) return best > 0.5 ? '4' : '5';
    return cotton(domeLight(u, v, lump, lx, ly) - (best > 0.8 ? 0.25 : 0));
  }
  // The pleated drum: lit on the near (left) side, a crease every 4 px, its top in the crown's shadow.
  const [left, right] = drumSides(v);
  if (v >= DRUM_TOP - 1 && v < BAND[0] + sag(u) && u >= left && u <= right) {
    const across = (u - left) / (right - left);
    const l = 0.95 - across * 1.15 - (v < -12.6 ? 0.45 : 0);
    const crease = Math.floor(u + 40) % 4 === 0;
    return cotton(crease ? l - 0.5 : l);
  }
  const skull = inEllipse(u, v, SKULL);
  // The band in the team's colour round the brow: lit along its top, stitched along its foot.
  if (v >= BAND[0] + sag(u) && v < BAND[1] + sag(u) && Math.abs(u) <= 20.5) {
    const into = v - BAND[0] - sag(u);
    if (into < 1) return 'c';
    if (into > 3.5) return 'B';
    return u > 13 ? 'B' : 'b';
  }
  // Dark hair cropped at the back, a sideburn down to the ear.
  if (skull <= 1 && v >= BAND[1] && (u < -13.5 || (u < -9 && v < 4.5))) return v < 0 && u > -17 ? '2' : '1';
  return null;
};

/** The middle of a wing's path at u (its spine), to light the half above it. */
function fromPathY(u: number, pts: readonly number[]): number {
  for (let i = 0; i + 3 < pts.length; i += 2) {
    const [x0, y0, x1, y1] = [pts[i], pts[i + 1], pts[i + 2], pts[i + 3]] as [number, number, number, number];
    if ((u - x0) * (u - x1) <= 0 && x0 !== x1) return y0 + ((y1 - y0) * (u - x0)) / (x1 - x0);
  }
  return Infinity;
}

// --- Chef's whites ---------------------------------------------------------------------------------------------------

/** The jacket standing (the frame's x 5-56, y 39-57): double-breasted whites, the team's neckerchief, an apron. */
function whites(): Part {
  const [x0, y0] = [5, 39];
  const c = new PixelCanvas(52, 19);
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  const inSuit = (x: number, y: number) => {
    if (y < 41 || y > 57) return false;
    if (x >= 16 && x <= 43) return true;
    for (const [cx, rx] of [
      [12.5, 7.5],
      [47, 6.5],
    ] as const) {
      const [dx, dy] = [(x + 0.5 - cx) / rx, (y + 0.5 - 47) / 5.8];
      if (dx * dx + dy * dy <= 1) return true;
    }
    return false;
  };
  for (let y = 41; y <= 57; y++)
    for (let x = 5; x <= 56; x++) {
      if (!inSuit(x, y)) continue;
      const lit = !inSuit(x - 1, y) || !inSuit(x, y - 1);
      const far = x > 41;
      // Folds in the cotton: a few soft creases down the sleeves.
      const fold = (x === 11 || x === 15 || x === 46) && y > 45;
      at(x, y, lit ? 'w' : far ? (!inSuit(x + 1, y) ? '4' : '5') : fold ? '5' : 'w');
    }
  // The double breast: its flap's edge down the front, in shade beside it.
  for (let y = 44; y <= 52; y++) {
    at(34, y, '4');
    at(35, y, '5');
  }
  // Two rows of black knot buttons.
  for (const x of [29, 39])
    for (const y of [45, 48, 51]) {
      at(x, y, '2');
      at(x + 1, y, '1');
      at(x, y + 1, '1');
      at(x + 1, y + 1, 'k');
    }
  // The mandarin collar and the team's neckerchief knotted at the throat, its end hanging.
  for (let x = 17; x <= 42; x++) {
    at(x, 41, x < 20 ? 'w' : '5');
    at(x, 42, x < 20 || x > 38 ? '5' : 'b');
  }
  c.stamp(
    `
    ccbbbbbbbbbbbbbbB
    .cbbbbcbbbbbBBB..
    ...cbbbbbB.......
    ....cbbBB........
    .....cbB.........
    ......B..........
  `,
    22 - x0,
    42 - y0,
  );
  // The apron strings in the team's colour round the waist, the apron under them.
  for (let x = 16; x <= 43; x++) {
    at(x, 53, x < 19 ? 'c' : 'b');
    at(x, 54, x > 40 ? 'B' : 'b');
    at(x, 55, x < 19 ? 'w' : x > 40 ? '5' : 'e');
    at(x, 56, x > 40 ? '4' : 'e');
    at(x, 57, (x + 1) % 5 === 0 ? '5' : 'e');
  }
  return [c, x0, y0];
}

/** The whites lying (right of the legs, under the head): the apron (left), the buttons, the neckerchief by the head. */
const DEAD_WHITES = grid(`
  ....5555
  ...5wwww
  ee5wwwwb
  ee5wwwwc
  ee5wwwwb
  ee5w2w2c
  ee5wwwwb
  ee5w4wwc
  ee5w4wbc
  ee5w4wbc
  ee5w4wwc
  ee5wwwwb
  ee5w2w2c
  ee5wwwwb
  ee5wwwwc
  ee5wwwwb
  ee55555B
  ....4444
`);

/** The team's trousers, black clogs. */
const WHITES = (): Outfit => ({ torso: whites(), dead: [DEAD_WHITES, 24, 51], legs: { n: '2', N: '1', m: '3' } });

// --- The Pepper Mill's wood on every other gun -----------------------------------------------------------------------

/** Turned beech (the default gun is its own, `pollinator_chef`): wood for steel. */
const BEECH = { '3': 'n', '4': 'm', '5': 't' } as const;

// --- Its flair -------------------------------------------------------------------------------------------------------

const WHITE = 0xffffff;
const SILVER = 0xc0cbdc;
const CREAM = 0xead4aa;
const PINK = 0xf6757a;
const RED = 0xe43b44;
const ORANGE = 0xf77622;
const RUST = 0xbe4a2f;

const steam: FlairPart = {
  kind: 'emit',
  at: { on: 'head', x: 31, y: 2 },
  box: [26, 3],
  rate: 4.5,
  v: [-2, 2, -13, -8],
  wobble: [2.5, 1.4],
  drag: 0.7,
  life: [1, 1.5],
  size: [2, 4],
  tones: [WHITE, WHITE, SILVER],
  alpha: [0.75, 0],
  layer: 'front',
};

const FLAIR: SetFlair = {
  head: [
    steam,
    // The rosy cheeks, glowing warm.
    {
      kind: 'pulse',
      at: { on: 'head', x: 26, y: 40 },
      r: [2.5, 3.5],
      alpha: [0.3, 0.45],
      period: 2.2,
      tone: PINK,
      layer: 'front',
    },
    {
      kind: 'pulse',
      at: { on: 'head', x: 43, y: 40 },
      r: [2, 3],
      alpha: [0.25, 0.4],
      period: 2.2,
      tone: PINK,
      layer: 'front',
    },
  ],
  outfit: [
    // A tea towel in the team's stripes tucked in the apron strings, swinging.
    {
      kind: 'chain',
      at: { on: 'torso', x: 10, y: 54 },
      n: 6,
      seg: 1.6,
      dir: Math.PI * 0.55,
      stiff: 3,
      g: 300,
      drag: 0.1,
      sway: [0.2, 0.8, 0],
      w: [5, 6, 6],
      shape: 'bars',
      tones: [WHITE, 'team', WHITE, WHITE, 'team', WHITE],
      rim: 0x3a4466,
      lit: WHITE,
      tatter: 0,
      layer: 'back',
    },
    // Puffs of flour off it on the run.
    {
      kind: 'emit',
      at: { on: 'torso', x: 26, y: 52 },
      box: [24, 8],
      rate: 0,
      run: 0.05,
      v: [-8, 4, -10, -2],
      g: 6,
      drag: 0.45,
      life: [0.5, 0.85],
      size: [2, 6],
      tones: [WHITE, WHITE, CREAM],
      alpha: [0.85, 0],
      layer: 'back',
    },
  ],
  gun: [
    // Embers glowing in the grinder's mouth, flecks of chilli falling out of it.
    {
      kind: 'pulse',
      at: { on: 'gun', x: 61, y: 20 },
      r: [2, 4],
      alpha: [0.2, 0.4],
      period: 0.7,
      tone: ORANGE,
      layer: 'gun',
    },
    {
      kind: 'emit',
      at: { on: 'gun', x: 61, y: 22 },
      box: [3, 2],
      rate: 3,
      v: [-2, 4, 2, 6],
      g: 40,
      drag: 0.6,
      life: [0.3, 0.55],
      size: [1, 1],
      tones: [RED, ORANGE, RUST],
      layer: 'gun',
    },
  ],
};

/** The Chef set (ADR-0160 amended). */
export const CHEF = drawnLater({ head: () => mask(toque), outfit: WHITES, gun: BEECH, flair: FLAIR });
